import {
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from "@mui/icons-material"
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import { FC, useCallback, useEffect, useMemo, useState } from "react"
import { SQSConfigDict } from "shared/sstv2Types"
import {
  useCreateSstv2SqsConfigMutation,
  useDeleteSstv2SqsConfigMutation,
  useGetSstv2SqsConfigsQuery,
  useUpdateSstv2SqsConfigMutation,
} from "src/features/partners/partnersApi"

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const maskSecret = (value: string | undefined | null): string => {
  if (!value) return ""
  if (value.length <= 4) return "****"
  return "****" + value.slice(-4)
}

const EMPTY_FORM: SQSConfigDict = {
  tag: "",
  name: "",
  queueARN: "",
  url: "",
  awsAccessKeyId: "",
  awsSecretAccessKey: "",
  region: "",
  eventSource: null,
  version: null,
  default: false,
}

type FormErrors = Partial<Record<keyof SQSConfigDict, string>>

const validateForm = (
  values: SQSConfigDict,
  mode: "create" | "edit",
): FormErrors => {
  const errors: FormErrors = {}
  if (!values.tag?.trim()) errors.tag = "Tag is required"
  if (!values.name?.trim()) errors.name = "Name is required"
  if (!values.queueARN?.trim()) errors.queueARN = "Queue ARN is required"
  else if (!values.queueARN.startsWith("arn:aws:sqs:"))
    errors.queueARN = "Must start with arn:aws:sqs:"
  if (!values.url?.trim()) errors.url = "URL is required"
  if (!values.awsAccessKeyId?.trim())
    errors.awsAccessKeyId = "Access Key ID is required"
  // On edit the secret is intentionally left blank to keep the existing value
  // (a masked secret can't be re-submitted). Only require it when creating.
  if (mode === "create" && !values.awsSecretAccessKey?.trim())
    errors.awsSecretAccessKey = "Secret Access Key is required"
  if (!values.region?.trim()) errors.region = "Region is required"
  return errors
}

// ---------------------------------------------------------------------------
// SqsConfigFormDialog
// ---------------------------------------------------------------------------

type SqsConfigFormDialogProps = {
  open: boolean
  onClose: () => void
  mode: "create" | "edit"
  initialValues?: SQSConfigDict
  onSubmit: (values: SQSConfigDict) => void
  isSubmitting: boolean
}

const SqsConfigFormDialog: FC<SqsConfigFormDialogProps> = ({
  open,
  onClose,
  mode,
  initialValues,
  onSubmit,
  isSubmitting,
}) => {
  const [form, setForm] = useState<SQSConfigDict>(EMPTY_FORM)
  const [errors, setErrors] = useState<FormErrors>({})
  const [touched, setTouched] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (open) {
      setForm(
        initialValues
          ? { ...initialValues, awsSecretAccessKey: "" }
          : { ...EMPTY_FORM },
      )
      setErrors({})
      setTouched(new Set())
    }
  }, [open, initialValues])

  const handleChange = useCallback(
    (field: keyof SQSConfigDict) =>
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setForm((prev) => ({ ...prev, [field]: value || null }))
        setTouched((prev) => new Set(prev).add(field))
      },
    [],
  )

  const handleBooleanChange = useCallback(
    (field: keyof SQSConfigDict) =>
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const checked = e.target.checked
        setForm((prev) => ({ ...prev, [field]: checked }))
        setTouched((prev) => new Set(prev).add(field))
      },
    [],
  )

  const handleSubmit = useCallback(() => {
    const errs = validateForm(form, mode)
    setErrors(errs)
    // Mark all fields as touched to show errors
    setTouched(
      new Set([
        "tag",
        "name",
        "queueARN",
        "url",
        "awsAccessKeyId",
        "awsSecretAccessKey",
        "region",
      ]),
    )
    if (Object.keys(errs).length === 0) {
      // The dialog blanks the secret input on open for display hygiene, but the
      // SST V2 PATCH requires awsSecretAccessKey (omitting it → 422). So when
      // editing without typing a new secret, restore the original value that
      // GET returned in initialValues — keeping the stored secret unchanged.
      if (mode === "edit" && !form.awsSecretAccessKey?.trim()) {
        onSubmit({
          ...form,
          awsSecretAccessKey: initialValues?.awsSecretAccessKey ?? "",
        })
      } else {
        onSubmit(form)
      }
    }
  }, [form, mode, initialValues, onSubmit])

  const showError = (field: keyof SQSConfigDict) =>
    touched.has(field) && errors[field] ? errors[field] : undefined

  return (
    <Dialog data-ui="UI-SL2W-240" open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {mode === "create" ? "Create SQS Config" : `Edit SQS Config`}
      </DialogTitle>
      <DialogContent>
        <Stack direction="column" spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Tag"
            value={form.tag ?? ""}
            onChange={handleChange("tag")}
            required
            disabled={mode === "edit"}
            error={!!showError("tag")}
            helperText={
              showError("tag") ||
              (mode === "edit" ? "Tag cannot be changed" : "Unique identifier")
            }
            size="small"
            fullWidth
          />
          <TextField
            label="Name"
            value={form.name ?? ""}
            onChange={handleChange("name")}
            required
            error={!!showError("name")}
            helperText={showError("name")}
            size="small"
            fullWidth
          />
          <TextField
            label="Queue ARN"
            value={form.queueARN ?? ""}
            onChange={handleChange("queueARN")}
            required
            error={!!showError("queueARN")}
            helperText={showError("queueARN") || "e.g. arn:aws:sqs:us-east-1:123456789:queue-name"}
            size="small"
            fullWidth
          />
          <TextField
            label="URL"
            value={form.url ?? ""}
            onChange={handleChange("url")}
            required
            error={!!showError("url")}
            helperText={showError("url")}
            size="small"
            fullWidth
          />
          <TextField
            label="AWS Access Key ID"
            value={form.awsAccessKeyId ?? ""}
            onChange={handleChange("awsAccessKeyId")}
            required
            error={!!showError("awsAccessKeyId")}
            helperText={showError("awsAccessKeyId")}
            size="small"
            fullWidth
          />
          <TextField
            label="AWS Secret Access Key"
            value={form.awsSecretAccessKey ?? ""}
            onChange={handleChange("awsSecretAccessKey")}
            required={mode === "create"}
            error={!!showError("awsSecretAccessKey")}
            helperText={
              showError("awsSecretAccessKey") ||
              (mode === "edit"
                ? "Leave blank to keep the current secret"
                : undefined)
            }
            size="small"
            fullWidth
            type="password"
          />
          <TextField
            label="Region"
            value={form.region ?? ""}
            onChange={handleChange("region")}
            required
            error={!!showError("region")}
            helperText={showError("region") || "e.g. us-east-1, ca-central-1"}
            size="small"
            fullWidth
          />
          <TextField
            label="Event Source"
            value={form.eventSource ?? ""}
            onChange={handleChange("eventSource")}
            size="small"
            fullWidth
            helperText="Optional"
          />
          <TextField
            label="Version"
            value={form.version ?? ""}
            onChange={handleChange("version")}
            size="small"
            fullWidth
            helperText="Optional"
          />
          <FormControlLabel
            control={
              <Switch
                checked={!!form.default}
                onChange={handleBooleanChange("default")}
                size="small"
              />
            }
            label="Default"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting}
          startIcon={
            isSubmitting ? <CircularProgress size={16} /> : undefined
          }
        >
          {mode === "create" ? "Create" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// DeleteConfirmDialog
// ---------------------------------------------------------------------------

type DeleteConfirmDialogProps = {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  config: SQSConfigDict | null
  isDeleting: boolean
}

const DeleteConfirmDialog: FC<DeleteConfirmDialogProps> = ({
  open,
  onClose,
  onConfirm,
  config,
  isDeleting,
}) => (
  <Dialog data-ui="UI-SL2W-241" open={open} onClose={onClose} maxWidth="xs" fullWidth>
    <DialogTitle>Delete SQS Config</DialogTitle>
    <DialogContent>
      <Stack direction="column" spacing={2}>
        <Typography variant="body1">
          Are you sure you want to delete this SQS config?
        </Typography>
        {config && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="body2">
              <strong>Tag:</strong> {config.tag}
            </Typography>
            <Typography variant="body2">
              <strong>Name:</strong> {config.name}
            </Typography>
          </Paper>
        )}
        <Alert severity="warning">This action cannot be undone.</Alert>
      </Stack>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} disabled={isDeleting}>
        Cancel
      </Button>
      <Button
        variant="contained"
        color="error"
        onClick={onConfirm}
        disabled={isDeleting}
        startIcon={
          isDeleting ? <CircularProgress size={16} /> : <DeleteIcon />
        }
      >
        Delete
      </Button>
    </DialogActions>
  </Dialog>
)

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

type SnackState = {
  open: boolean
  message: string
  severity: "success" | "error"
}

export const SaSstv2SqsConfigs: FC = () => {
  const qSqsConfigs = useGetSstv2SqsConfigsQuery({})
  const configs = useMemo(() => qSqsConfigs.data?.data || [], [qSqsConfigs.data])

  const [createSqsConfig, createResult] = useCreateSstv2SqsConfigMutation()
  const [updateSqsConfig, updateResult] = useUpdateSstv2SqsConfigMutation()
  const [deleteSqsConfig, deleteResult] = useDeleteSstv2SqsConfigMutation()

  // Dialog state
  const [formOpen, setFormOpen] = useState(false)
  const [formMode, setFormMode] = useState<"create" | "edit">("create")
  const [editingConfig, setEditingConfig] = useState<SQSConfigDict | undefined>(
    undefined,
  )
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deletingConfig, setDeletingConfig] = useState<SQSConfigDict | null>(
    null,
  )

  // Snackbar
  const [snack, setSnack] = useState<SnackState>({
    open: false,
    message: "",
    severity: "success",
  })
  const showSnack = useCallback(
    (message: string, severity: "success" | "error") => {
      setSnack({ open: true, message, severity })
    },
    [],
  )
  const handleSnackClose = useCallback(() => {
    setSnack((prev) => ({ ...prev, open: false }))
  }, [])

  // Handlers
  const handleOpenCreate = useCallback(() => {
    setFormMode("create")
    setEditingConfig(undefined)
    setFormOpen(true)
  }, [])

  const handleOpenEdit = useCallback((config: SQSConfigDict) => {
    setFormMode("edit")
    setEditingConfig(config)
    setFormOpen(true)
  }, [])

  const handleFormClose = useCallback(() => {
    setFormOpen(false)
    setEditingConfig(undefined)
  }, [])

  const handleFormSubmit = useCallback(
    async (values: SQSConfigDict) => {
      try {
        if (formMode === "create") {
          await createSqsConfig(values).unwrap()
          showSnack(`SQS config "${values.tag}" created successfully`, "success")
        } else {
          await updateSqsConfig({
            tag: values.tag,
            data: values,
          }).unwrap()
          showSnack(
            `SQS config "${values.tag}" updated successfully`,
            "success",
          )
        }
        setFormOpen(false)
        setEditingConfig(undefined)
      } catch (err: any) {
        const msg =
          err?.data?.message || err?.message || "An error occurred"
        showSnack(
          `Failed to ${formMode} SQS config: ${msg}`,
          "error",
        )
      }
    },
    [formMode, createSqsConfig, updateSqsConfig, showSnack],
  )

  const handleOpenDelete = useCallback((config: SQSConfigDict) => {
    setDeletingConfig(config)
    setDeleteOpen(true)
  }, [])

  const handleDeleteClose = useCallback(() => {
    setDeleteOpen(false)
    setDeletingConfig(null)
  }, [])

  const handleDeleteConfirm = useCallback(async () => {
    if (!deletingConfig) return
    try {
      await deleteSqsConfig({ tag: deletingConfig.tag }).unwrap()
      showSnack(
        `SQS config "${deletingConfig.tag}" deleted successfully`,
        "success",
      )
      setDeleteOpen(false)
      setDeletingConfig(null)
    } catch (err: any) {
      const msg =
        err?.data?.message || err?.message || "An error occurred"
      showSnack(`Failed to delete SQS config: ${msg}`, "error")
    }
  }, [deletingConfig, deleteSqsConfig, showSnack])

  const isSubmitting = createResult.isLoading || updateResult.isLoading

  return (
    <Stack data-ui="UI-SL2W-239" direction="column" spacing={2} width="100%">
      <Stack direction="row" spacing={2} alignItems="center">
        <Typography variant="body1" fontWeight="bold">
          {`SQS Configs (${configs.length})`}
        </Typography>
        {(qSqsConfigs.isLoading || qSqsConfigs.isFetching) && (
          <CircularProgress size={18} />
        )}
        <div style={{ flex: 1 }} />
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleOpenCreate}
        >
          Add Config
        </Button>
      </Stack>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Tag</strong>
              </TableCell>
              <TableCell>
                <strong>Name</strong>
              </TableCell>
              <TableCell>
                <strong>Queue ARN</strong>
              </TableCell>
              <TableCell>
                <strong>URL</strong>
              </TableCell>
              <TableCell>
                <strong>Region</strong>
              </TableCell>
              <TableCell>
                <strong>Access Key</strong>
              </TableCell>
              <TableCell>
                <strong>Secret Key</strong>
              </TableCell>
              <TableCell>
                <strong>Event Source</strong>
              </TableCell>
              <TableCell>
                <strong>Version</strong>
              </TableCell>
              <TableCell align="center">
                <strong>Default</strong>
              </TableCell>
              <TableCell align="right">
                <strong>Actions</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {configs.map((config, index) => (
              <TableRow key={`${config.tag}-${index}`}>
                <TableCell>
                  <Typography variant="caption" fontWeight="bold">
                    {config.tag}
                  </Typography>
                </TableCell>
                <TableCell>{config.name}</TableCell>
                <TableCell>
                  <Typography
                    variant="caption"
                    fontFamily="monospace"
                    sx={{ wordBreak: "break-all" }}
                  >
                    {config.queueARN}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    variant="caption"
                    fontFamily="monospace"
                    sx={{ wordBreak: "break-all" }}
                  >
                    {config.url}
                  </Typography>
                </TableCell>
                <TableCell>{config.region}</TableCell>
                <TableCell>
                  <Typography variant="caption" fontFamily="monospace">
                    {maskSecret(config.awsAccessKeyId)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" fontFamily="monospace">
                    {maskSecret(config.awsSecretAccessKey)}
                  </Typography>
                </TableCell>
                <TableCell>{config.eventSource || "-"}</TableCell>
                <TableCell>{config.version || "-"}</TableCell>
                <TableCell align="center">
                  {config.default ? (
                    <CheckCircleIcon
                      color="primary"
                      fontSize="small"
                      titleAccess="Default"
                    />
                  ) : (
                    "-"
                  )}
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenEdit(config)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleOpenDelete(config)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {!qSqsConfigs.isLoading && configs.length === 0 && (
              <TableRow>
                <TableCell colSpan={11} align="center">
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ py: 3 }}
                  >
                    No SQS configs found. Click "Add Config" to create one.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create / Edit Dialog */}
      <SqsConfigFormDialog
        open={formOpen}
        onClose={handleFormClose}
        mode={formMode}
        initialValues={editingConfig}
        onSubmit={handleFormSubmit}
        isSubmitting={isSubmitting}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteOpen}
        onClose={handleDeleteClose}
        onConfirm={handleDeleteConfirm}
        config={deletingConfig}
        isDeleting={deleteResult.isLoading}
      />

      {/* Snackbar for success/error feedback */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={handleSnackClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackClose}
          severity={snack.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Stack>
  )
}

export default SaSstv2SqsConfigs
